import type { ReactNode, VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION, VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import FeatureFlagRequired from '../../generic/Gates/FeatureFlagRequired';
import Toggle from '../../generic/Toggle';

import Section from './SettingsPageSection';

import styles from './QuickImportScanMethodsSection.module.less';

type QuickImportScanMethodsScopeKind = 'project' | 'organization';

type Props = {
  scopeType: QuickImportScanMethodsScopeKind;
  isVendoredDetectionEnabled: boolean;
  onToggleVendoredDetection: (isChecked: boolean) => void;
  isSnippetDetectionEnabled: boolean;
  onToggleSnippetDetection: (isChecked: boolean) => void;
  isDisabled?: boolean;
  buttons?: ReactNode;
};

const scopeText: Record<QuickImportScanMethodsScopeKind, { heading: string; paragraph: string }> = {
  project: {
    heading: 'Quick Import',
    paragraph: 'Choose which components are scanned for this project.',
  },
  organization: {
    heading: 'Quick Import Defaults',
    paragraph: 'Choose which components are scanned by default when importing new projects.',
  },
};

/**
 * QuickImportScanMethodsSection renders the scan methods toggles for vendored dependencies
 * and snippets. Parameterized for use in both project and organization settings.
 *
 * Each toggle is independently gated by its respective feature flag.
 * The entire section should only be rendered if at least one flag is enabled;
 * that gating is the responsibility of the parent component.
 */
const QuickImportScanMethodsSection: VoidFunctionComponent<Props> = ({
  scopeType,
  isVendoredDetectionEnabled,
  onToggleVendoredDetection,
  isSnippetDetectionEnabled,
  onToggleSnippetDetection,
  isDisabled,
  buttons,
}) => {
  const text = scopeText[scopeType];

  return (
    <Section>
      <Section.Left heading={text.heading}>
        <Section.Paragraph>{text.paragraph}</Section.Paragraph>
        {buttons}
      </Section.Left>
      <Section.Right>
        <FeatureFlagRequired flagName={VENDORED_DEPENDENCY_DETECTION}>
          <div className={styles.toggleRow}>
            <div className={styles.toggleLabel}>
              <span className={styles.toggleTitle}>Scan for Vendored Dependencies</span>
              <span className={styles.toggleDescription}>
                Detect third-party code included directly in your repository.
              </span>
            </div>
            <Toggle
              aria-label="Scan for Vendored Dependencies"
              checked={isVendoredDetectionEnabled}
              onChange={onToggleVendoredDetection}
              isDisabled={isDisabled}
            />
          </div>
        </FeatureFlagRequired>
        <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
          <div className={styles.toggleRow}>
            <div className={styles.toggleLabel}>
              <span className={styles.toggleTitle}>Scan for Snippets</span>
              <span className={styles.toggleDescription}>
                Detect copied code fragments during analysis.
              </span>
            </div>
            <Toggle
              aria-label="Scan for Snippets"
              checked={isSnippetDetectionEnabled}
              onChange={onToggleSnippetDetection}
              isDisabled={isDisabled}
            />
          </div>
        </FeatureFlagRequired>
      </Section.Right>
    </Section>
  );
};

export default QuickImportScanMethodsSection;
